import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, PlayCircle } from "lucide-react";
import { fetchPublicLot, fetchPublicMeta, mediaSrc, b2bBase } from "@/b2b/b2bApi";
import {
  CATEGORY_LABELS, CATEGORY_FIELDS, STATUS_CONFIG, itemTitle, itemSpecSummary, fmtMoney,
} from "@/b2b/constants";

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.AVAILABLE;
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ${cfg.cls}`}>{cfg.label}</span>;
}

function buildWaMessage(lot, waNumber) {
  const lines = [
    `Hello RentOPC, I'm enquiring about lot ${lot.lot_number} (${lot.lot_name}).`,
    `Category: ${CATEGORY_LABELS[lot.category] || lot.category}`,
    "",
  ];
  (lot.items || []).slice(0, 12).forEach((it) => {
    const bits = [itemTitle(lot.category, it), itemSpecSummary(lot.category, it)].filter(Boolean).join(" — ");
    const price = fmtMoney(it.price_per_unit);
    lines.push(`• ${it.available_quantity} × ${bits}${price ? ` @ ${price}/pc` : ""}`);
  });
  lines.push("", "Please share availability and best price.");
  return lines.join("\n");
}

export default function B2BLotDetailPage() {
  const { lotNumber } = useParams();
  const base = b2bBase();
  const [lot, setLot] = useState(null);
  const [wa, setWa] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  useEffect(() => {
    fetchPublicLot(lotNumber).then(setLot).catch(() => setErr("Lot not found or no longer available."));
    fetchPublicMeta().then((mt) => setWa(mt?.whatsapp_number || null)).catch(() => {});
  }, [lotNumber]);

  const images = useMemo(() => (lot?.media || []).filter((m) => m.media_type === "image"), [lot]);
  const waLink = useMemo(() => {
    if (!lot || !wa) return null;
    const digits = String(wa).replace(/\D/g, "");
    if (!digits) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(buildWaMessage(lot, wa))}`;
  }, [lot, wa]);

  if (err) return (
    <div className="min-h-screen bg-[#f5f7fa] flex flex-col items-center justify-center gap-3 text-slate-600">
      <div data-testid="b2b-detail-error">{err}</div>
      <Link to={`${base}/`} className="text-sm text-slate-800 underline">Back to catalogue</Link>
    </div>
  );
  if (!lot) return <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center text-slate-400 text-sm">Loading…</div>;

  const fields = CATEGORY_FIELDS[lot.category] || CATEGORY_FIELDS.other;

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#17202a]" data-testid="b2b-lot-detail">
      <header className="sticky top-0 z-10 bg-[#111827] text-white shadow-[0_2px_12px_#0002]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-4">
          <Link to={`${base}/`} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white" data-testid="b2b-back">
            <ArrowLeft size={15} /> Back to catalogue
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-slate-500 font-mono">{lot.lot_number} · {CATEGORY_LABELS[lot.category]}</div>
            <h1 className="text-2xl font-extrabold tracking-tight mt-0.5">{lot.lot_name}</h1>
            <div className="mt-1 text-sm text-slate-600">
              {lot.total_available_quantity} of {lot.total_original_quantity} available
              {lot.total_value != null && <> · lot value {fmtMoney(lot.total_value)}</>}
            </div>
          </div>
          <StatusBadge status={lot.status} />
        </div>

        {/* Gallery */}
        {images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="b2b-gallery">
            {images.map((m) => (
              <div key={m.id} className="aspect-video rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                <img src={mediaSrc(m.url)} alt={lot.lot_name} loading="lazy" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* Line items */}
        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Line Items ({lot.items.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="b2b-line-items">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Item</th>
                  {fields.filter((f) => !["brand", "model", "product_name"].includes(f.key)).map((f) => (
                    <th key={f.key} className="text-left px-3 py-2 whitespace-nowrap">{f.label}</th>
                  ))}
                  <th className="text-right px-3 py-2">Avail</th>
                  <th className="text-right px-4 py-2">Price/pc</th>
                </tr>
              </thead>
              <tbody>
                {lot.items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100 align-top" data-testid={`line-item-${it.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold">{itemTitle(lot.category, it)}</div>
                      {it.remarks && <div className="text-xs text-slate-500 mt-0.5">{it.remarks}</div>}
                    </td>
                    {fields.filter((f) => !["brand", "model", "product_name"].includes(f.key)).map((f) => (
                      <td key={f.key} className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{it[f.key] || "—"}</td>
                    ))}
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                      {it.available_quantity}/{it.original_quantity}
                    </td>
                    <td className="px-4 py-2.5 text-right font-bold whitespace-nowrap">{fmtMoney(it.price_per_unit) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {lot.remarks && (
          <div className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-600">{lot.remarks}</div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          {lot.video_url && (
            <a href={lot.video_url} target="_blank" rel="noreferrer" data-testid="b2b-video"
              className="inline-flex items-center justify-center gap-2 flex-1 min-w-[160px] rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50">
              <PlayCircle size={16} /> Watch Video
            </a>
          )}
          {waLink ? (
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="b2b-whatsapp"
              className="inline-flex items-center justify-center gap-2 flex-1 min-w-[160px] rounded-xl bg-[#16a34a] px-4 py-3 text-sm font-bold text-white hover:bg-[#15803d]">
              <MessageCircle size={16} /> Enquire on WhatsApp
            </a>
          ) : (
            <span data-testid="b2b-whatsapp-unset"
              className="inline-flex items-center justify-center gap-2 flex-1 min-w-[160px] rounded-xl bg-slate-200 px-4 py-3 text-sm font-bold text-slate-500">
              <MessageCircle size={16} /> WhatsApp enquiry unavailable
            </span>
          )}
        </div>
      </main>
    </div>
  );
}
